import React, { useState } from "react";
import axios from "axios";
import {
  Calculator, IndianRupee, TrendingUp, PieChart, CheckCircle, XCircle,
  AlertTriangle, ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";

const formatINR = (v) => {
  if (!v) return "0";
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
};

const EMICalculatorPage = () => {
  const [form, setForm] = useState({
    property_value: 7500000,
    down_payment_pct: 20,
    loan_tenure_years: 20,
    interest_rate: 8.5,
    monthly_income: 150000,
    stamp_duty_pct: 6,
    registration_pct: 1,
    under_construction: false,
  });
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const calculate = async () => {
    setCalculating(true);
    try {
      const res = await axios.post(`${API}/decision-support/emi-calculator`, form);
      setResult(res.data);
    } catch (err) {
      toast.error("Calculation failed");
    } finally {
      setCalculating(false);
    }
  };

  const af = result?.affordability;

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="emi-calculator-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">EMI & Affordability Calculator</h1>
          <p className="text-sm text-slate-500 mt-1">Calculate EMI, total costs, and buyer affordability</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Input Form */}
          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="text-base">Loan Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Property Value</label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="number" className="pl-9" value={form.property_value}
                    onChange={(e) => setForm({ ...form, property_value: parseInt(e.target.value) || 0 })} data-testid="emi-property-value" />
                </div>
                <p className="text-xs text-slate-400 mt-1">{formatINR(form.property_value)}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Down Payment %</label>
                  <Input type="number" value={form.down_payment_pct} onChange={(e) => setForm({ ...form, down_payment_pct: parseInt(e.target.value) || 0 })} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Tenure (years)</label>
                  <Input type="number" value={form.loan_tenure_years} onChange={(e) => setForm({ ...form, loan_tenure_years: parseInt(e.target.value) || 1 })} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Interest Rate (%)</label>
                <Input type="number" step="0.1" value={form.interest_rate}
                  onChange={(e) => setForm({ ...form, interest_rate: parseFloat(e.target.value) || 0 })} data-testid="emi-interest-rate" />
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Monthly Income (for affordability)</label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="number" className="pl-9" value={form.monthly_income}
                    onChange={(e) => setForm({ ...form, monthly_income: parseInt(e.target.value) || 0 })} data-testid="emi-monthly-income" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Stamp Duty %</label>
                  <Input type="number" step="0.5" value={form.stamp_duty_pct} onChange={(e) => setForm({ ...form, stamp_duty_pct: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Registration %</label>
                  <Input type="number" step="0.5" value={form.registration_pct} onChange={(e) => setForm({ ...form, registration_pct: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.under_construction} onCheckedChange={(v) => setForm({ ...form, under_construction: v })} />
                <label className="text-sm text-slate-700">Under Construction (5% GST)</label>
              </div>
              <Button className="w-full" onClick={calculate} disabled={calculating} data-testid="calculate-emi-btn">
                <Calculator className="w-4 h-4 mr-2" /> {calculating ? "Calculating..." : "Calculate EMI"}
              </Button>
            </CardContent>
          </Card>

          {/* Results */}
          <div className="lg:col-span-3 space-y-4">
            {!result ? (
              <Card><CardContent className="p-12 text-center text-slate-400"><Calculator className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>Enter details and click Calculate to see results</p></CardContent></Card>
            ) : (
              <>
                {/* EMI Highlight */}
                <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
                  <CardContent className="p-6 text-center">
                    <p className="text-sm text-slate-300 mb-1">Monthly EMI</p>
                    <p className="text-4xl font-bold font-['Manrope']" data-testid="emi-result">{formatINR(result.monthly_emi)}</p>
                    <p className="text-xs text-slate-400 mt-2">for {result.tenure_years} years at {result.interest_rate}% p.a.</p>
                  </CardContent>
                </Card>

                {/* Breakdown */}
                <div className="grid grid-cols-3 gap-3">
                  <Card><CardContent className="p-3 text-center">
                    <p className="text-xs text-slate-500">Loan Amount</p>
                    <p className="text-lg font-bold text-slate-900">{formatINR(result.loan_amount)}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-3 text-center">
                    <p className="text-xs text-slate-500">Total Interest</p>
                    <p className="text-lg font-bold text-red-600">{formatINR(result.total_interest)}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-3 text-center">
                    <p className="text-xs text-slate-500">Total Payment</p>
                    <p className="text-lg font-bold text-slate-900">{formatINR(result.total_payment)}</p>
                  </CardContent></Card>
                </div>

                {/* Principal vs Interest */}
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm font-medium text-slate-700 mb-3">Principal vs Interest Split</p>
                    <div className="flex h-6 rounded-full overflow-hidden">
                      <div className="bg-blue-500 flex items-center justify-center" style={{ width: `${result.principal_pct}%` }}>
                        <span className="text-[10px] font-bold text-white">{result.principal_pct}%</span>
                      </div>
                      <div className="bg-red-400 flex items-center justify-center" style={{ width: `${result.interest_pct}%` }}>
                        <span className="text-[10px] font-bold text-white">{result.interest_pct}%</span>
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>Principal: {formatINR(result.loan_amount)}</span>
                      <span>Interest: {formatINR(result.total_interest)}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Additional Costs */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Additional Costs</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-slate-600">Stamp Duty</span><span className="font-medium">{formatINR(result.additional_costs.stamp_duty)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-600">Registration</span><span className="font-medium">{formatINR(result.additional_costs.registration)}</span></div>
                    {result.additional_costs.gst > 0 && <div className="flex justify-between text-sm"><span className="text-slate-600">GST</span><span className="font-medium">{formatINR(result.additional_costs.gst)}</span></div>}
                    <Separator />
                    <div className="flex justify-between text-sm font-bold"><span>Total Cost</span><span className="text-emerald-700">{formatINR(result.total_cost)}</span></div>
                  </CardContent>
                </Card>

                {/* Affordability */}
                {af && (
                  <Card className={`border-2 ${af.status === "comfortable" ? "border-emerald-200 bg-emerald-50/30" : af.status === "stretched" ? "border-amber-200 bg-amber-50/30" : "border-red-200 bg-red-50/30"}`}
                    data-testid="affordability-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        {af.status === "comfortable" ? <CheckCircle className="w-5 h-5 text-emerald-500" /> :
                         af.status === "stretched" ? <AlertTriangle className="w-5 h-5 text-amber-500" /> :
                         <XCircle className="w-5 h-5 text-red-500" />}
                        Affordability: <span className="capitalize">{af.status?.replace(/_/g, " ")}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-slate-600">EMI-to-Income Ratio</span>
                          <span className={`text-sm font-bold ${af.emi_to_income_ratio <= 30 ? "text-emerald-600" : af.emi_to_income_ratio <= 40 ? "text-amber-600" : "text-red-600"}`}>{af.emi_to_income_ratio}%</span>
                        </div>
                        <Progress value={af.emi_to_income_ratio} className="h-2" />
                        <p className="text-xs text-slate-400 mt-1">Recommended: below 40%</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><p className="text-slate-500">Max Affordable EMI</p><p className="font-bold text-slate-900">{formatINR(af.max_affordable_emi)}/mo</p></div>
                        <div><p className="text-slate-500">Max Eligible Loan</p><p className="font-bold text-slate-900">{formatINR(af.max_eligible_loan)}</p></div>
                        <div className="col-span-2"><p className="text-slate-500">Max Property Value (at {form.down_payment_pct}% down)</p><p className="font-bold text-blue-600">{formatINR(af.max_property_value)}</p></div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Yearly Breakup */}
                {result.yearly_breakup?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Year-wise Breakup</CardTitle></CardHeader>
                    <CardContent>
                      <div className="max-h-64 overflow-y-auto">
                        <div className="space-y-1">
                          {result.yearly_breakup.map((yr) => (
                            <div key={yr.year} className="flex items-center gap-2 text-xs py-1 border-b last:border-0">
                              <span className="w-10 text-slate-500 font-medium">Y{yr.year}</span>
                              <div className="flex-1 flex h-3 rounded-full overflow-hidden bg-slate-100">
                                <div className="bg-blue-500" style={{ width: `${(yr.principal / (yr.principal + yr.interest)) * 100}%` }} />
                                <div className="bg-red-400" style={{ width: `${(yr.interest / (yr.principal + yr.interest)) * 100}%` }} />
                              </div>
                              <span className="w-16 text-right text-slate-600">{formatINR(yr.remaining)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EMICalculatorPage;
